"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.recordCommunicationEvents = exports.listWebsiteCommunications = exports.RECORD_EVENTS_URL = exports.LIST_COMMUNICATIONS_URL = exports.metricsPublisher = void 0;
const darter_katal_metrics_1 = require("@amzn/darter-katal-metrics");
exports.LIST_COMMUNICATIONS_URL = '/communications/api/website-communications';
exports.RECORD_EVENTS_URL = '/communications/api/communications-events';
const BATCH_EVENTS_MAX_COUNT = 25;
const FAIL_PUT_COUNT_MAX = 2;
const RECORD_EVENTS_INTERVAL_MILLIS = 10000;
/**
 * Makes a GET request to the given API (URL), handling errors and type conversion (wrapper around the browser's fetch).
 */
const fetchFromServer = async (url, queryParams) => {
    let formattedUrl = url;
    if (queryParams) {
        const params = new URLSearchParams(queryParams);
        formattedUrl += `?${params.toString()}`;
    }
    const response = await fetch(formattedUrl);
    if (!response.ok) {
        throw new Error(`An error occurred while fetching from ${url}`);
    }
    return await response.json();
};
/**
 * Attempts to fetch the result (Promise) for the given API (URL) from the result store, calling the supplied fetcher
 * if the store or store result doesn't exist. If the fetcher is invoked, the result is added to the store.
 */
const fetchResult = (url, fetcher) => {
    const resultStore = (window.__BRAND_REGISTRY_API_RESULTS__ ?? {});
    const existingResult = resultStore[url];
    if (existingResult instanceof Promise) {
        return existingResult;
    }
    const result = fetcher(url);
    resultStore[url] = result;
    window.__BRAND_REGISTRY_API_RESULTS__ = resultStore;
    return result;
};
const listWebsiteCommunications = async (communicationType) => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const communicationsResponse = await fetchResult(exports.LIST_COMMUNICATIONS_URL, (url) => fetchFromServer(url, { timeZone }));
    return communicationsResponse.communications[communicationType] ?? [];
};
exports.listWebsiteCommunications = listWebsiteCommunications;
let failPutCount = 0;
/**
 * Makes a PUT request to the given API (URL), handling errors and type conversion (wrapper around the browser's fetch).
 */
const putToServer = async (url, body) => {
    const response = await fetch(url, {
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        method: 'PUT',
    });
    if (!response.ok) {
        failPutCount += 1;
        throw new Error(`An error occurred while submitting a put request to ${url}`);
    }
    else {
        failPutCount = 0;
    }
    return await response.json();
};
const unsentEvents = [];
let isIntervalPaused = false;
const publishRecordEventsFailureMetrics = (events) => {
    exports.metricsPublisher.publishCounterMonitor('RecordEventsFailure', 1);
    events.forEach(({ communicationId, eventType }) => {
        exports.metricsPublisher.publishString(`RecordEventsFailure:${eventType}`, communicationId);
    });
};
// send a batch of events at a regular interval
const recordCommunicationEventsAtInterval = async () => {
    const eventsToSend = [...unsentEvents];
    if (eventsToSend.length && !isIntervalPaused && failPutCount < FAIL_PUT_COUNT_MAX) {
        const requestBody = { events: eventsToSend };
        await putToServer(exports.RECORD_EVENTS_URL, requestBody)
            .catch(() => publishRecordEventsFailureMetrics(eventsToSend));
        unsentEvents.splice(0, eventsToSend.length);
    }
};
setInterval(recordCommunicationEventsAtInterval, RECORD_EVENTS_INTERVAL_MILLIS);
// if the page is closed, we want to send the remainder of the unsent events
window.onunload = () => (0, exports.recordCommunicationEvents)([], true);
const recordCommunicationEvents = async (events, publishAllEvents = false, appName = 'Unknown', customerId = '') => {
    if (!exports.metricsPublisher) {
        exports.metricsPublisher = (0, darter_katal_metrics_1.useMetrics)(customerId, appName, 'BrandRegistryCommunicationServiceTypeScriptClient');
    }
    // Pause the interval while sending a batch of events
    if (publishAllEvents || unsentEvents.length + events.length >= BATCH_EVENTS_MAX_COUNT) {
        isIntervalPaused = true;
    }
    // add new events to the queue
    unsentEvents.push(...events);
    const eventsToSendCount = publishAllEvents ? unsentEvents.length : BATCH_EVENTS_MAX_COUNT;
    while (unsentEvents.length && (unsentEvents.length >= eventsToSendCount && failPutCount < FAIL_PUT_COUNT_MAX)) {
        // Send events in batches
        const eventsBatch = unsentEvents.slice(0, BATCH_EVENTS_MAX_COUNT);
        const requestBody = { events: eventsBatch };
        await putToServer(exports.RECORD_EVENTS_URL, requestBody)
            .catch(() => publishRecordEventsFailureMetrics(eventsBatch));
        // remove the now sent events from the queue
        unsentEvents.splice(0, BATCH_EVENTS_MAX_COUNT);
    }
    // Unpause the interval
    isIntervalPaused = false;
};
exports.recordCommunicationEvents = recordCommunicationEvents;
